var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { $settings } from 'ziphy-web-shared/basic/entities/settings';
import { useLoader } from 'ziphy-web-shared/basic/hooks';
import { WithLoaderWrapper } from 'ziphy-web-shared/basic/lib/loaders';
import { globalLoaderHandlers, localStore, useLoaderSubscribe } from 'ziphy-web-shared/basic/utils';
import styles from './Notes.module.scss';
import { $notes } from './Notes.store';
import Section from './section/Section';
import SectionAddAddendum from './section/sectionAddAddendum/SectionAddAddendum';
import SectionAddendum from './section/sectionAddendum/SectionAddendum';
import SectionSign from './section/sectionSign/SectionSign';
import FloatBar from './ui/floatBar/FloatBar';
import Sidebar from './ui/sidebar/Sidebar';
import SidebarContent from './ui/sidebarContent/SidebarContent';
import TopBar from './ui/topBar/TopBar';
const Notes = ({ practice, appointment, encounter, patient, isStartProvider, isLoading: isApptLoading, containerClassName, showToolbars = true, }) => {
    var _a;
    const { t } = useTranslation();
    const STORAGE_KEY_UNLOCKED = 'notes_unlock_processed_encounter_ids';
    const unlockedForEncounterIdsRef = React.useRef(new Set((_a = localStore.get(STORAGE_KEY_UNLOCKED)) !== null && _a !== void 0 ? _a : []));
    const { isLoading } = useLoader(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!encounter || isApptLoading)
            return;
        yield $notes.open({ practice, appointment, encounter, patient, isStartProvider });
    }), [
        encounter === null || encounter === void 0 ? void 0 : encounter.id,
        encounter === null || encounter === void 0 ? void 0 : encounter.startTime,
        encounter === null || encounter === void 0 ? void 0 : encounter.cancelTime,
        encounter === null || encounter === void 0 ? void 0 : encounter.finishTime,
        isApptLoading,
    ]);
    useEffect(() => {
        if (isLoading)
            return;
        $notes.UPDATE_CONTEXT({ practice, appointment, encounter, patient, isStartProvider });
    }, [encounter, appointment, patient]);
    useEffect(() => () => $notes.clear(true), []);
    useLoaderSubscribe(globalLoaderHandlers.EVENT, ({ eventTypes, events }) => __awaiter(void 0, void 0, void 0, function* () {
        var _b, _c;
        if (eventTypes.has('encounter_started') &&
            events.some((x) => x.encounterId === $notes.encounter.id)) {
            const encounterIdStr = String($notes.encounter.id);
            if (unlockedForEncounterIdsRef.current.has(encounterIdStr))
                return;
            const initialReasonId = (_c = (_b = $settings.optionsByScope['reason_for_editing_notes']) === null || _b === void 0 ? void 0 : _b.find((s) => s.key === 'initial')) === null || _c === void 0 ? void 0 : _c.id;
            if (initialReasonId) {
                unlockedForEncounterIdsRef.current.add(encounterIdStr);
                localStore.set(STORAGE_KEY_UNLOCKED, Array.from(unlockedForEncounterIdsRef.current));
                yield $notes.unlock(initialReasonId);
            }
        }
    }));
    return (_jsx(WithLoaderWrapper, Object.assign({ id: 'notes_wrapper', isLoading: isLoading, loader: _jsx(_Fragment, {}), className: styles.wrapper }, { children: _jsx("div", Object.assign({ className: classNames(styles.container, 'container', containerClassName) }, { children: $notes.encounter ? (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ className: styles.inner }, { children: [showToolbars && _jsx(TopBar, { className: classNames(styles.topBar, 'mb-20') }), _jsxs("div", Object.assign({ className: styles.content }, { children: [_jsxs("div", Object.assign({ className: styles.sections }, { children: [$notes.sections.map(($section) => (_jsx(Section, { "$section": $section }, $section.id))), $notes.addendum.length > 0 && _jsx(SectionAddendum, { data: $notes.addendum }), ($notes.isEditable || $notes.isSigned) && _jsx(SectionSign, {}), $notes.isAddendumAvailable && _jsx(SectionAddAddendum, {})] })), showToolbars && _jsx(SidebarContent, { className: styles.sidebarContent })] })), _jsx(FloatBar, {})] })), showToolbars && _jsx(Sidebar, { className: styles.sidebar })] })) : (!isApptLoading &&
                ($notes.openError ? (_jsx("div", Object.assign({ className: styles.noResults }, { children: t('error.something_went_wrong_try_again_later') }))) : (_jsx("div", Object.assign({ className: styles.noResults }, { children: t('text.appt_no_encounters') }))))) })) })));
};
export default observer(Notes);
